import { useMemo, useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { useWorkOrderDetailData } from '@pages/Work/WorkOrderProfilePage/hooks';
import { useWorkOrderPartsData } from '@pages/Work/WorkOrderProfilePage/hooks/useWorkOrderPartsData';
import { useWorkOrderChargesData } from '@pages/Work/WorkOrderProfilePage/hooks/useWorkOrderChargesData';
import { dcRuntimeKeys } from '@api/bff/calibrationRuntime/constants';
import useOnlineStatus from '@hooks/useOnlineStatus';
import { isWorkOrderLocked } from '@utils/workOrderHelpers';
import { toLegacyGuid } from '@utils/uuid';
import { db } from '@db/database';
import { useLocalPhotos } from './useLocalPhotos';
import { useOfflineCalibrations } from './useOfflineCalibrations';
import { fetchCalibrationRuntimeByWoDetail, fetchEquipment } from '../services';
import {
  calculateOverallResults,
  getCoverPhotoData,
  getPromotedPhotosData,
  convertLocalPhotosToCalibrationPhotos,
  hasUnsyncedLocalPhotos,
  parseCalibrationData,
  determineWorkStatus,
  determineEventStatus
} from '../utils';
import type { EquipmentReviewData, CustomerReviewData } from '../types';
import type { CalibrationRuntimeData } from '@/features/calibration/hooks/useCalibrationRuntime/types';

export const useCustomerReviewData = (
  jobNumber: string | undefined
): CustomerReviewData => {
  const isOnline = useOnlineStatus();

  const {
    workOrder,
    details,
    isLoading: isWorkOrderLoading,
    error: workOrderError
  } = useWorkOrderDetailData({ jobNumber: jobNumber || '' });

  const { parts, isLoading: isPartsLoading } = useWorkOrderPartsData(
    jobNumber || ''
  );

  const { charges, isLoading: isChargesLoading } = useWorkOrderChargesData(
    jobNumber || ''
  );

  const { localPhotosMap, isLoaded: localPhotosLoaded } = useLocalPhotos(
    jobNumber,
    details
  );

  const { offlineCalibrations, isLoading: offlineCalibrationLoading } =
    useOfflineCalibrations(isOnline, jobNumber, details);

  const calibrationQueries = useQueries({
    queries: details.map(detail => {
      const woDetailGlobalId = toLegacyGuid(detail.globalId || '');
      return {
        queryKey: dcRuntimeKeys.byWoDetailGlobalId(woDetailGlobalId),
        queryFn: () => fetchCalibrationRuntimeByWoDetail(woDetailGlobalId),
        enabled: isOnline && !!detail.globalId,
        staleTime: isOnline ? 0 : Infinity,
        refetchOnMount: isOnline ? ('always' as const) : false
      };
    })
  });

  const equipmentQueries = useQueries({
    queries: details.map(detail => ({
      queryKey: ['equipment', detail.companyName, detail.id],
      queryFn: () => fetchEquipment(detail.companyName || '', detail.id || ''),
      enabled: isOnline && !!detail.companyName && !!detail.id,
      staleTime: 5 * 60 * 1000
    }))
  });

  // Write back online calibration data to IndexedDB for offline access
  const calibrationDataTimestamp = calibrationQueries
    .map(q => q.dataUpdatedAt)
    .join(',');

  useEffect(() => {
    if (!isOnline) return;

    calibrationQueries.forEach((query, index) => {
      if (!query.isSuccess || !query.data || !details[index]?.globalId) return;

      const woDetailGlobalId = toLegacyGuid(details[index].globalId || '');
      db.cachedCalibrationRuntimes
        .where('woDetailGlobalId')
        .equals(woDetailGlobalId)
        .first()
        .then(existing => {
          if (existing) {
            return db.cachedCalibrationRuntimes.update(existing.id!, {
              ...query.data,
              cachedAt: new Date().toISOString(),
              _userId: existing._userId
            });
          }
        })
        .catch(() => {
          // Non-critical
        });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOnline, calibrationDataTimestamp]);

  const isCalibrationLoading = isOnline
    ? calibrationQueries.some(q => q.isLoading)
    : offlineCalibrationLoading;

  const isEquipmentLoading = isOnline
    ? equipmentQueries.some(q => q.isLoading)
    : false;

  const equipmentList: EquipmentReviewData[] = useMemo(() => {
    return details.map((detail, index) => {
      // Prefer React Query cache (retains data when queries are disabled offline),
      // fall back to IndexedDB only when cache was never populated (e.g. hard refresh offline)
      const serverRuntime = calibrationQueries[index]?.isFetched
        ? calibrationQueries[index]?.data ?? null
        : !isOnline
          ? offlineCalibrations.get(detail.globalId || '') ?? null
          : null;

      const serverCalData = parseCalibrationData(serverRuntime?.jsonData);
      const localPhotos = localPhotosMap.get(detail.globalId || '') || [];
      const serverPhotos = serverCalData?.photos || [];

      const serverHasSyncedPhotos =
        serverPhotos?.some(p => p.syncStatus === 'synced') ?? false;

      const photosToUse = serverHasSyncedPhotos
        ? serverPhotos
        : localPhotos.length > 0
          ? convertLocalPhotosToCalibrationPhotos(localPhotos)
          : serverPhotos;

      const photosAreUnsynced = hasUnsyncedLocalPhotos(
        localPhotos,
        serverPhotos
      );

      const calDataToUse: CalibrationRuntimeData | null = serverCalData
        ? { ...serverCalData, photos: photosToUse }
        : null;

      const calculatedResults = calculateOverallResults(calDataToUse);
      const equipment = equipmentQueries[index]?.data;
      const coverPhotoData = getCoverPhotoData(calDataToUse);
      const promotedPhotosData = getPromotedPhotosData(
        calDataToUse,
        photosAreUnsynced
      );

      return {
        sequenceNumber: detail.sequenceNumber || 0,
        assetId: detail.id || '',
        woDetailGlobalId: detail.globalId || null,
        manufacturer: equipment?.manufacturer || null,
        modelNumber: equipment?.modelNumber || null,
        description: equipment?.description || null,
        coverImageUrl: coverPhotoData.url,
        coverPhotoIndex: coverPhotoData.index,
        coverPhotoIsUnsynced: photosAreUnsynced && coverPhotoData.url !== null,
        promotedPhotos: promotedPhotosData.photos,
        promotedPhotoIndices: promotedPhotosData.indices,
        eventNum: serverRuntime?.eventNum ?? null,
        hasUnsyncedPhotos: photosAreUnsynced,
        testsCompleted: calculatedResults.completedPoints,
        totalTests: calculatedResults.totalPoints,
        asFoundResult:
          calculatedResults.totalPoints > 0 &&
          calculatedResults.completedPoints > 0
            ? calculatedResults.asFoundPassed
              ? 'pass'
              : 'fail'
            : null,
        asLeftResult:
          calculatedResults.totalPoints > 0 &&
          calculatedResults.completedPoints > 0
            ? calculatedResults.asLeftPassed
              ? 'pass'
              : 'fail'
            : null,
        workStatus: determineWorkStatus(detail.status),
        eventStatus: determineEventStatus(calDataToUse),
        inspectionNotes: calDataToUse?.assessment?.inspectionNotes || null,
        conditionAsFound: calDataToUse?.assessment?.conditionAsFound || null,
        conditionAsLeft: calDataToUse?.assessment?.conditionAsLeft || null,
        operabilityAsFound:
          calDataToUse?.assessment?.operabilityAsFound || null,
        operabilityAsLeft: calDataToUse?.assessment?.operabilityAsLeft || null
      };
    });
  }, [
    details,
    calibrationQueries,
    equipmentQueries,
    isOnline,
    offlineCalibrations,
    localPhotosMap,
    localPhotosLoaded
  ]);

  const isLoading =
    isWorkOrderLoading ||
    isPartsLoading ||
    isChargesLoading ||
    isCalibrationLoading ||
    isEquipmentLoading;

  const isLocked = useMemo(
    () => isWorkOrderLocked(workOrder?.status),
    [workOrder?.status]
  );

  return {
    workOrder,
    equipmentList,
    parts: parts || [],
    charges: charges || [],
    isLoading,
    error: workOrderError,
    isOffline: !isOnline,
    isLocked
  };
};
